import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Stack } from '@mui/material';
import { Form, Formik } from 'formik';
import moment from 'moment/moment';

import ThreeLevelSteps from '../../../common/steps';
import AdditionalOptions from './common/additional_options';
import useNotification from '../../../../../core/hooks/use_notification';
import DestinationsList from './common/destinations_list';
import { getFoodPartnerNotificationData } from '../../../../../core/utils/notification_utils';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../core/constants';
import { getFoodPartnerAction } from '../../../../../features/food_partner/get_food_partner';
import { getAllPodsState } from '../../../../../features/pod/get_all_pods';
import AddPodDestinationDialog from "../../../common/warehouse_and_food_partner_order_form/add_pod_destination_dialog";
import SectionPaper from '../../../../common/section_paper';
import BottomActionBar from '../../../../common/bottom_action_bar';
import { Button, SubmitButton } from '../../../../common/button';

const PZAndMMForm = ({ form, setView, data, setData }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getAllPods = useSelector(getAllPodsState);

    useEffect(() => {
        dispatch(getFoodPartnerAction(form?.foodPartnerId))
            .then(response => showErrorMessage(getFoodPartnerNotificationData(response)));
    }, [dispatch, form, showErrorMessage]);

    const initialValues = {
        destinationsList: data?.formik?.destinationsList || [],
        productsList: data?.formik?.productsList || [],
        attachments: data?.formik?.attachments || [],
    };

    const onReturn = () => {
        setData({});
        setView(0);
    };

    const onChangeSetDate = (newDate, allDates, formik) => {
        formik?.values?.destinationsList?.forEach((_, index) => {
            const productsList = formik?.values?.productsList[index]?.map(product => {
                if (!allDates && product?.expirationDate) {
                    return product;
                }

                return ({ ...product, expirationDate: newDate });
            });

            if (productsList?.length !== 0) {
                formik.setFieldValue(`productsList.${ index }`, productsList);
            }
        });
    };

    const onCopyHistoricalDocument = (values, formik) => {
        const destinationsList = [];
        const productsList = [];

        if (!getAllPods?.data) return;

        Object.keys(values)?.forEach((key, index) => {
            destinationsList.push({
                position: index,
                name: getAllPods?.data?.map(pod => ({
                    id: pod?.id,
                    label: pod?.indoorPartner?.name + ' (' + pod?.description + ') - ' + (pod?.device?.serviceId || 'brak urządzenia')
                })).find(pod => pod?.id === key)?.label,
                destinationPodId: key
            });

            productsList[index] = values?.[key]?.map((product, index) => ({
                position: index,
                name: product?.productName,
                productId: product?.productId,
                productBatchId: '',
                quantity: product?.quantity,
                expirationDate: product?.defaultDaysToExpiration ? moment(form?.deliveryDate).add(product?.defaultDaysToExpiration - 1, 'days') : '',
                price: product?.purchaseNetPrice || '',
                type: product?.type
            }));
        });

        formik.setFieldValue('destinationsList', destinationsList);
        formik.setFieldValue('productsList', productsList);
    };

    const handleSubmit = (values) => {
        const documents = [
            ...values.destinationsList.map((destination, index) => (
                {
                    destinationPodId: destination.destinationPodId,
                    documentProducts: [
                        ...values?.productsList?.[index]?.map(product => ({
                            productId: product?.productId,
                            quantity: product?.quantity,
                            expirationDate: product?.expirationDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
                            price: product?.price
                        }))
                    ]
                }
            ))
        ];

        setData({
            form: {
                ...form,
                deliveryDate: form?.deliveryDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT),
                documents,
                attachments: values?.attachments || []
            },
            formik: values
        });
        setView(5);
    };

    const validate = (values) => {
        const errors = {};
        const productsListErrors = [];
        let hasErrors = false;

        if (!values.destinationsList || values.destinationsList.length === 0) {
            errors.destinationsList = 'Wybierz minimum 1 lokalizację';
            return errors;
        }

        values.destinationsList.forEach((_, index) => {
            if (!values.productsList[index] || values.productsList[index].length === 0) {
                productsListErrors[index] = 'Wybierz minimum 1 produkt';
                hasErrors = true;
            } else {
                const itemErrorsArray = [];
                values.productsList[index].forEach((product, productIndex) => {
                    const itemErrors = {};

                    if (!product?.quantity) {
                        itemErrors.quantity = 'Pole wymagane';
                    } else if (product?.quantity <= 0) {
                        itemErrors.quantity = 'Proszę podać wartość całkowitą dodatnią';
                    }

                    if (!product?.expirationDate) {
                        itemErrors.expirationDate = 'Pole wymagane';
                    }

                    if (!product?.price) {
                        itemErrors.price = 'Pole wymagane';
                    } else if (product?.price <= 0) {
                        itemErrors.price = 'Proszę podać wartość dodatnią';
                    }

                    if (Object.keys(itemErrors).length > 0) {
                        itemErrorsArray[productIndex] = itemErrors;
                        hasErrors = true;
                    }
                });

                if (itemErrorsArray.length > 0 || productsListErrors[index]) {
                    productsListErrors[index] = productsListErrors[index] || itemErrorsArray;
                }
            }
        });

        if (hasErrors) {
            errors.productsList = productsListErrors;
        }

        return errors;
    };

    return (
        <Formik
            initialValues={ initialValues }
            validate={ validate }
            onSubmit={ handleSubmit }
        >
            { (formik) => (
                <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', gap: 3, flexGrow: 1 } }>
                    <Box sx={ { flexGrow: 1, pb: 4 } }>
                        <Stack spacing={ 3 }>
                            <ThreeLevelSteps secondStep/>
                            <AdditionalOptions
                                onChangeSetDate={ (newDate, allDates) => onChangeSetDate(newDate, allDates, formik) }
                                form={ form }
                                onCopyHistoricalDocument={ (values) => onCopyHistoricalDocument(values, formik) }
                            />

                            <SectionPaper title="Magazyny docelowe i produkty">
                                <Stack spacing={ 3 }>
                                    <DestinationsList
                                        form={ form }
                                        formik={ formik }
                                    />
                                    <AddPodDestinationDialog
                                        formik={ formik }
                                    />
                                </Stack>
                            </SectionPaper>
                        </Stack>
                    </Box>

                    <BottomActionBar>
                        <Box sx={ { display: 'flex', justifyContent: 'space-between', width: '100%' } }>
                            <Button variant="outlined" onClick={ onReturn }>Wróć</Button>
                            <SubmitButton type="button" onClick={ formik.submitForm }>Dalej</SubmitButton>
                        </Box>
                    </BottomActionBar>
                </Box>
            ) }
        </Formik>
    );
};

export default PZAndMMForm;